import { getBackendUrl } from './backendUrl';
import { getAuthHeaders } from '../auth/authHeaders';

class ConfigProvider {
  private cache: any = {};
  private cacheTime = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly REQUIRED_CONFIGS = [
    'auth_token',
    'jwt_secret',
    'tracking_url',
    'default_sender_email', 
    'default_sender_name',
    'aws_region',
    'aws_access_key_id',
    'aws_secret_access_key',
    'sqs_queue_url',
    'unsubscribe_worker_url',
    'grafana_url',
    'backend_api_url',
    'debounce_api_key',
    'bearer_token',
    'kumomta_metrics_url',
    'dynamodb_table_name',
    'r2_domain',
    'r2_images_domain'
  ];

  private async fetchConfig(): Promise<any> {
    const now = Date.now();
    if (this.cache && (now - this.cacheTime) < this.CACHE_TTL) {
      return this.cache;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/config`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      this.cache = data;
      this.cacheTime = now;
    } catch (error) {
      console.error('Error fetching config:', error);
      this.cache = {};
    }
    return this.cache;
  }

  async get(key: string): Promise<string> {
    const config = await this.fetchConfig();
    return config[key] || '';
  }

  async isConfigured(): Promise<boolean> {
    const config = await this.fetchConfig();
    return this.REQUIRED_CONFIGS.every(key => config[key] && config[key].trim() !== '');
  }

  async getMissingConfigs(): Promise<string[]> {
    const config = await this.fetchConfig();
    return this.REQUIRED_CONFIGS.filter(key => !config[key] || config[key].trim() === '');
  }

  async update(key: string, value: string): Promise<boolean> {
    try {
      const response = await fetch(`${getBackendUrl()}/api/config/${key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value })
      });
      if (response.ok) {
        this.cache[key] = value;
        // Backend automatically invalidates cache on update
      }
      return response.ok;
    } catch (error) {
      console.error('Error updating config:', error);
      return false;
    }
  }

  clearCache(): void {
    this.cache = {};
    this.cacheTime = 0;
  }
}

const configProvider = new ConfigProvider();

export const getTrackingUrl = (): Promise<string> => {
  return configProvider.get('tracking_url');
};

export const isConfigured = (): Promise<boolean> => {
  return configProvider.isConfigured();
};

export const getMissingConfigs = (): Promise<string[]> => {
  return configProvider.getMissingConfigs();
};

export const getGrafanaUrl = (): Promise<string> => {
  return configProvider.get('grafana_url');
};

export const updateConfig = (key: string, value: string): Promise<boolean> => {
  return configProvider.update(key, value);
};

export const getConfig = (key: string): Promise<string> => {
  return configProvider.get(key);
};

export const getDynamoDbTableName = (): Promise<string> => {
  return configProvider.get('dynamodb_table_name');
};

export const getR2Domain = (): Promise<string> => {
  return configProvider.get('r2_domain');
};

export const getDefaultTimeFilter = async (): Promise<string> => {
  const value = await configProvider.get('default_time_filter');
  return value || ''; // Default to empty (show all) if not set
};

export const getCsvChunkSize = async (): Promise<number> => {
  const value = await configProvider.get('csv_chunk_size');
  return parseInt(value) || 0;
};

export const getCsvExportChunkSize = async (): Promise<number> => {
  const value = await configProvider.get('csv_export_chunk_size');
  return parseInt(value) || 0;
};

export const clearConfigCache = async (): Promise<void> => {
  configProvider.clearCache();
  
  // Also invalidate backend cache
  try {
    await fetch(`${getBackendUrl()}/api/config/purge-cache`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
  } catch (error) {
    console.warn('Failed to purge backend cache:', error);
  }
};

export const getMailServerManagementUrl = (): Promise<string> => {
  return configProvider.get('mail_server_management_url');
};